import React, { useState, useEffect, useCallback } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { toast } from '../components/Toast';
import { formatCurrency } from '../utils/currency';
import './FinancialTransactions.css';

const formatDate = (d) =>
  new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const MODE_LABELS = { CASH: 'Cash', BANK: 'Bank', UPI: 'UPI', CHEQUE: 'Cheque' };

function VendorPaymentsPage() {
  const { isCompanyAdmin } = useAuth();
  const [formData, setFormData] = useState({
    transactionType: 'PAYMENT',
    transactionDate: new Date().toISOString().split('T')[0],
    branchId: '',
    payeeType: 'VENDOR',
    vendorId: '',
    employeeId: '',
    payeeName: '',
    projectId: '',
    accountHeadId: '',
    paymentAccountHeadId: '',
    paymentMode: 'CASH',
    amount: '',
    referenceNo: '',
    notes: ''
  });

  const [branches, setBranches]         = useState([]);

  const [vendors, setVendors]           = useState([]);
  const [employees, setEmployees]       = useState([]);
  const [projects, setProjects]         = useState([]);
  const [accountHeads, setAccountHeads] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [vendorOutstanding, setVendorOutstanding] = useState(null);
  const [recentPayments, setRecentPayments]       = useState([]);
  const [editingTxn, setEditingTxn]               = useState(null);
  const [loading, setLoading]                     = useState(false);
  const [listLoading, setListLoading]   = useState(false);
  const [showNewAccountHeadModal, setShowNewAccountHeadModal] = useState(false);
  const [newAccountHeadForm, setNewAccountHeadForm] = useState({
    name: '',
    description: ''
  });
  const [listSearch, setListSearch] = useState('');
  const [viewingTxn, setViewingTxn] = useState(null);
  const [historyTxn, setHistoryTxn] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (historyTxn) {
      fetchHistory(historyTxn.id);
    }
  }, [historyTxn]);

  const fetchHistory = async (txnId) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`/financial-transactions/${txnId}/approval-history`);
      setHistoryList(res.data?.data || []);
    } catch (err) {
      toast.error('Failed to load approval history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const renderStatus = (txn) => {
    const status = txn.status || 'APPROVED';
    let bg = '#e2e8f0', color = '#475569', label = status;
    if (status === 'APPROVED') { bg = '#dcfce7'; color = '#15803d'; label = 'Approved'; }
    else if (status === 'REJECTED') { bg = '#fee2e2'; color = '#b91c1c'; label = 'Rejected'; }
    else if (status === 'PENDING_L1') { bg = '#fef3c7'; color = '#d97706'; label = 'Pending L1'; }
    else if (status === 'PENDING_L2') { bg = '#f3e8ff'; color = '#7e22ce'; label = 'Pending L2'; }
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
        <span 
          style={{ 
            backgroundColor: bg, 
            color: color, 
            padding: '2px 8px', 
            borderRadius: '4px', 
            fontSize: '11px', 
            fontWeight: '600',
            textTransform: 'uppercase',
            display: 'inline-block'
          }}
        >
          {label}
        </span>
        <button
          type="button"
          onClick={() => setHistoryTxn(txn)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '14px' }}
          title="View Approval History"
        >
          ℹ️
        </button>
      </div>
    );
  };

  const renderHistoryModal = () => {
    if (!historyTxn) return null;
    return (
      <div className="modal-overlay" style={{ zIndex: 1200, display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }} onClick={() => setHistoryTxn(null)}>
        <div className="modal-content" style={{ maxWidth: '500px', background: 'white', borderRadius: '8px', width: '90%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
          <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>Approval History - {historyTxn.voucherNo}</h2>
            <button type="button" className="close-button" onClick={() => setHistoryTxn(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
          </div>
          <div className="modal-body" style={{ padding: '20px', maxHeight: '400px', overflowY: 'auto' }}>
            {historyLoading ? (
              <div>Loading history...</div>
            ) : historyList.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>No history logs found (Bypassed or legacy transaction).</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {historyList.map((h, i) => {
                  let actColor = 'var(--text-main)';
                  if (h.action?.includes('APPROVE')) actColor = 'green';
                  if (h.action?.includes('REJECT')) actColor = 'red';
                  return (
                    <div key={h.id} style={{ display: 'flex', gap: '12px', borderBottom: i < historyList.length - 1 ? '1px solid #eee' : 'none', paddingBottom: '10px' }}>
                      <div style={{ fontSize: '18px' }}>
                        {h.action?.includes('APPROVE') ? '✅' : h.action?.includes('REJECT') ? '❌' : '📤'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: '600', color: actColor }}>{h.action}</span>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{new Date(h.performedAt).toLocaleString()}</span>
                        </div>
                        <div style={{ fontSize: '13px', marginTop: '2px' }}>
                          By: <strong>{h.performedByName}</strong> ({h.performedByRole})
                        </div>
                        {h.remarks && (
                          <div style={{ fontSize: '13px', background: '#f8f9fa', padding: '6px', borderRadius: '4px', marginTop: '4px', color: '#555', fontStyle: 'italic' }}>
                            Remarks: "{h.remarks}"
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={() => setHistoryTxn(null)}>Close</button>
          </div>
        </div>
      </div>
    );
  };
  const [currentPage, setCurrentPage] = useState(1);
  const paymentsPerPage = 10;
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);

  useEffect(() => {
    if (isCompanyAdmin()) fetchBranches();
    fetchVendors();
    fetchEmployees();
    fetchProjects();
    fetchAccountHeads();
    fetchPaymentAccounts();
    fetchRecentPayments();
  }, []);

  const fetchBranches = async () => {
    try {
      const res = await axios.get('/company/branches');
      const data = res.data.data || res.data;
      setBranches(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (formData.payeeType === 'VENDOR' && formData.vendorId) {
      fetchVendorOutstanding(formData.vendorId);
    } else {
      setVendorOutstanding(null);
    }
  }, [formData.vendorId, formData.payeeType]);

  const fetchVendors = async () => {
    try {
      const res = await axios.get('/vendors?size=1000');
      const data = res.data.data?.content || res.data.content || res.data.data || [];
      setVendors(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get('/employees?size=1000');
      const data = res.data?.employees || res.data?.data?.employees || res.data?.data || [];
      setEmployees(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get('/projects?size=1000');
      const data = res.data.data?.content || res.data.content || res.data.data || [];
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const fetchAccountHeads = async () => {
    try {
      const res = await axios.get('/account-heads');
      const data = res.data.data || res.data || [];
      // Show only EXPENSE heads for payment categorization
      setAccountHeads((Array.isArray(data) ? data : []).filter(h => h.category === 'EXPENSE'));
    } catch (e) { console.error(e); }
  };

  const fetchPaymentAccounts = async () => {
    try {
      const res = await axios.get('/account-heads/category/PAYMENT_ACCOUNT');
      const data = res.data.data || res.data || [];
      setPaymentAccounts(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const fetchVendorOutstanding = async (vendorId) => {
    try {
      const res = await axios.get(`/financial-transactions/vendor/${vendorId}/outstanding`);
      setVendorOutstanding(res.data.data || res.data);
    } catch (e) { setVendorOutstanding(null); }
  };

  const handleCreateAccountHead = async () => {
    if (!newAccountHeadForm.name.trim()) return;
    try {
      const response = await axios.post('/account-heads', {
        name: newAccountHeadForm.name.trim(),
        description: newAccountHeadForm.description.trim(),
        category: 'EXPENSE',
        active: true
      });
      const data = response.data?.data || response.data;
      if (data && data.id) {
        await fetchAccountHeads();
        setFormData(prev => ({
          ...prev,
          accountHeadId: String(data.id)
        }));
      }
      setShowNewAccountHeadModal(false);
      setNewAccountHeadForm({ name: '', description: '' });
    } catch (err) {
      console.error('Failed to create account head:', err);
      alert(err.response?.data?.message || 'Failed to create account head');
    }
  };

  const fetchRecentPayments = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await axios.get('/financial-transactions/type/PAYMENT');
      const data = res.data.data || res.data || [];
      setRecentPayments(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setListLoading(false); }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'accountHeadId' && value === 'ADD_NEW') {
      setShowNewAccountHeadModal(true);
      setFormData(prev => ({ ...prev, accountHeadId: '' }));
      return;
    }
    if (name === 'paymentAccountHeadId') {
      const selectedAcc = paymentAccounts.find(a => String(a.id) === value);
      const isCash = selectedAcc?.name?.toLowerCase().includes('cash');
      setFormData(prev => ({
        ...prev,
        paymentAccountHeadId: value,
        paymentMode: isCash ? 'CASH' : 'BANK',
        referenceNo: isCash ? '' : prev.referenceNo
      }));
      return;
    }
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'payeeType') {
        updated.vendorId = '';
        updated.employeeId = '';
        updated.payeeName = '';
      }
      return updated;
    });
  };

  const handleEdit = (txn) => {
    setEditingTxn(txn);
    const payeeType = txn.vendorId ? 'VENDOR' : txn.employeeId ? 'EMPLOYEE' : txn.payeeName ? 'OTHER' : 'VENDOR';
    setFormData({
      transactionType: 'PAYMENT',
      transactionDate:      txn.transactionDate      || new Date().toISOString().split('T')[0],
      branchId:             txn.branchId             ? String(txn.branchId)             : '',
      payeeType,
      vendorId:             txn.vendorId             ? String(txn.vendorId)             : '',
      employeeId:           txn.employeeId           ? String(txn.employeeId)           : '',
      payeeName:            txn.payeeName            || '',
      projectId:            txn.projectId            ? String(txn.projectId)            : '',
      accountHeadId:        txn.accountHeadId        ? String(txn.accountHeadId)        : '',
      paymentAccountHeadId: txn.paymentAccountHeadId ? String(txn.paymentAccountHeadId) : '',
      paymentMode:          txn.paymentMode          || 'CASH',
      amount:               txn.amount               ? String(txn.amount)               : '',
      referenceNo:          txn.referenceNo          || '',
      notes:                txn.notes                || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingTxn(null);
    setFormData(prev => ({
      ...prev,
      branchId: '', payeeType: 'VENDOR',
      vendorId: '', employeeId: '', payeeName: '',
      projectId: '', accountHeadId: '', paymentAccountHeadId: '',
      paymentMode: 'CASH', amount: '', referenceNo: '', notes: ''
    }));
    setVendorSearch('');
    setEmployeeSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (isCompanyAdmin() && !formData.branchId && !editingTxn) {
      toast.error('Please select a branch');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        transactionType:      formData.transactionType,
        transactionDate:      formData.transactionDate,
        branchId:             formData.branchId             ? parseInt(formData.branchId)             : null,
        paymentMode:          formData.paymentMode,
        amount:               String(formData.amount),
        referenceNo:          formData.referenceNo          || null,
        notes:                formData.notes                || null,
        projectId:            formData.projectId            ? parseInt(formData.projectId)            : null,
        accountHeadId:        formData.accountHeadId        ? parseInt(formData.accountHeadId)        : null,
        paymentAccountHeadId: formData.paymentAccountHeadId ? parseInt(formData.paymentAccountHeadId) : null,
        vendorId:         formData.payeeType === 'VENDOR'   && formData.vendorId   ? parseInt(formData.vendorId)   : null,
        employeeId:       formData.payeeType === 'EMPLOYEE' && formData.employeeId ? parseInt(formData.employeeId) : null,
        payeeName:        formData.payeeType === 'OTHER'    ? formData.payeeName   : null,
      };

      let txn;
      if (editingTxn) {
        const res = await axios.put(`/financial-transactions/${editingTxn.id}`, payload);
        txn = res.data.data || res.data;
        toast.success(`Updated! Voucher: ${txn.voucherNo}`);
        setEditingTxn(null);
      } else {
        const res = await axios.post('/financial-transactions', payload);
        txn = res.data.data || res.data;
        toast.success(`Payment recorded! Voucher: ${txn.voucherNo}`);
      }

      setFormData(prev => ({
        ...prev,
        branchId: '',
        payeeType: 'VENDOR',
        vendorId: '', employeeId: '', payeeName: '',
        projectId: '', accountHeadId: '', paymentAccountHeadId: '',
        paymentMode: 'CASH', amount: '', referenceNo: '', notes: ''
      }));
      setVendorSearch('');
      setEmployeeSearch('');
      setVendorOutstanding(null);
      fetchRecentPayments();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
    } finally { setLoading(false); }
  };

  // Resolve display name for recent list
  const payeeName = (txn) =>
    txn.vendorName || txn.employeeName || txn.payeeName || '—';

  const selectedAccountName = paymentAccounts.find(a => String(a.id) === formData.paymentAccountHeadId)?.name || '';
  const isCashAccount = selectedAccountName.toLowerCase().includes('cash');

  // Filter recent payments based on search query
  const filteredPayments = recentPayments.filter(txn => {
    const name = payeeName(txn).toLowerCase();
    const vch = (txn.voucherNo || '').toLowerCase();
    const q = listSearch.toLowerCase();
    return name.includes(q) || vch.includes(q);
  });

  // Calculate pagination details
  const indexOfLastPayment = currentPage * paymentsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstPayment, indexOfLastPayment);
  const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="financial-transactions">
      <h1>Payments</h1>
      <p>Record payments to vendors, employees or other payees</p>

      {/* Centered stacked layout wrapper */}
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* ── Form ── */}
        <div className="transaction-form-container" style={{ width: '100%' }}>
          <h2 className="section-title">{editingTxn ? `Editing ${editingTxn.voucherNo}` : 'New Payment'}</h2>
          <form onSubmit={handleSubmit} className="transaction-form" style={{ maxWidth: '100%' }}>

            {/* Branch selector for COMPANY_ADMIN */}
            {isCompanyAdmin() && (
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Branch *</label>
                <select name="branchId" value={formData.branchId} onChange={handleChange} required={isCompanyAdmin()}>
                  <option value="">Select Branch</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}

            {/* Payee type selector */}
            <div className="form-group">
              <label>Pay To *</label>
              <div className="payee-type-tabs">
                {['VENDOR', 'EMPLOYEE', 'OTHER'].map(t => (
                  <button
                    key={t}
                    type="button"
                    className={`payee-tab ${formData.payeeType === t ? 'active' : ''}`}
                    onClick={() => handleChange({ target: { name: 'payeeType', value: t } })}
                  >
                    {t === 'VENDOR' ? '🏪 Vendor' : t === 'EMPLOYEE' ? '👤 Employee' : '✏️ Other'}
                  </button>
                ))}
              </div>
            </div>

            {/* Vendor outstanding */}
            {formData.payeeType === 'VENDOR' && vendorOutstanding && (
              <div className="outstanding-info">
                <h3>Vendor Outstanding</h3>
                <div className="outstanding-details">
                  <div className="outstanding-item"><span>Total Purchases</span><strong>{formatCurrency(vendorOutstanding.totalPurchases)}</strong></div>
                  <div className="outstanding-item"><span>Total Payments</span><strong>{formatCurrency(vendorOutstanding.totalPayments)}</strong></div>
                  <div className="outstanding-item highlight"><span>Outstanding</span><strong>{formatCurrency(vendorOutstanding.outstandingBalance)}</strong></div>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Payment Date *</label>
                <input type="date" name="transactionDate" value={formData.transactionDate} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Payment Account *</label>
                <select name="paymentAccountHeadId" value={formData.paymentAccountHeadId} onChange={handleChange} required>
                  <option value="">Select Account</option>
                  {paymentAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              {!isCashAccount && (
                <div className="form-group">
                  <label>Payment Mode</label>
                  <select name="paymentMode" value={formData.paymentMode} onChange={handleChange}>
                    <option value="BANK">Bank Transfer</option>
                    <option value="UPI">UPI</option>
                    <option value="CHEQUE">Cheque</option>
                  </select>
                </div>
              )}
              <div className="form-group" style={{ gridColumn: isCashAccount ? 'span 2' : 'span 1' }}>
                <label>Amount *</label>
                <input type="number" name="amount" value={formData.amount} onChange={handleChange}
                  placeholder="0.00" step="0.01" min="0.01" required />
              </div>
            </div>

            {/* Payee selection */}
            {formData.payeeType === 'VENDOR' && (
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Vendor *</label>
                <input
                  type="text"
                  placeholder="Type to search & select vendor..."
                  value={vendorDropdownOpen ? vendorSearch : (vendors.find(v => String(v.id) === formData.vendorId)?.name || '')}
                  onFocus={() => {
                    setVendorSearch('');
                    setVendorDropdownOpen(true);
                  }}
                  onChange={(e) => setVendorSearch(e.target.value)}
                  onBlur={() => setTimeout(() => setVendorDropdownOpen(false), 200)}
                  required
                  style={{ cursor: 'pointer' }}
                />
                {vendorDropdownOpen && (
                  <div className="custom-select-dropdown" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    background: 'var(--bg-card, #ffffff)',
                    border: '1px solid var(--border, #ddd)',
                    borderRadius: '6px',
                    boxShadow: 'var(--shadow, 0 4px 12px rgba(0,0,0,0.1))',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    marginTop: '4px'
                  }}>
                    {vendors
                      .filter(v => v.name.toLowerCase().includes(vendorSearch.toLowerCase()))
                      .map(v => (
                        <div
                          key={v.id}
                          className="custom-select-option"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setFormData(prev => ({ ...prev, vendorId: String(v.id) }));
                            setVendorDropdownOpen(false);
                            setVendorSearch('');
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            borderBottom: '1px solid var(--border, #f0f0f0)',
                            backgroundColor: String(v.id) === formData.vendorId ? 'var(--menu-active-bg, #e8f4ff)' : 'transparent',
                            color: String(v.id) === formData.vendorId ? 'var(--menu-active-text, #0056b3)' : 'var(--text-primary, inherit)'
                          }}
                          onMouseEnter={(e) => {
                            if (String(v.id) !== formData.vendorId) e.target.style.backgroundColor = 'var(--border, #f5f5f5)';
                          }}
                          onMouseLeave={(e) => {
                            if (String(v.id) !== formData.vendorId) e.target.style.backgroundColor = 'transparent';
                          }}
                        >
                          {v.name}
                        </div>
                      ))
                    }
                    {vendors.filter(v => v.name.toLowerCase().includes(vendorSearch.toLowerCase())).length === 0 && (
                      <div style={{ padding: '8px 12px', color: 'var(--text-secondary, #999)', fontSize: '13px', textAlign: 'center' }}>
                        No vendors found
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {formData.payeeType === 'EMPLOYEE' && (
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Employee *</label>
                <input
                  type="text"
                  placeholder="Type to search & select employee..."
                  value={employeeDropdownOpen ? employeeSearch : (
                    (() => {
                      const emp = employees.find(e => String(e.id) === formData.employeeId);
                      if (!emp) return '';
                      return `${emp.userName || emp.name} ${emp.employeeCode ? `(${emp.employeeCode})` : ''}`;
                    })()
                  )}
                  onFocus={() => {
                    setEmployeeSearch('');
                    setEmployeeDropdownOpen(true);
                  }}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  onBlur={() => setTimeout(() => setEmployeeDropdownOpen(false), 200)}
                  required
                  style={{ cursor: 'pointer' }}
                />
                {employeeDropdownOpen && (
                  <div className="custom-select-dropdown" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    background: 'var(--bg-card, #ffffff)',
                    border: '1px solid var(--border, #ddd)',
                    borderRadius: '6px',
                    boxShadow: 'var(--shadow, 0 4px 12px rgba(0,0,0,0.1))',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    marginTop: '4px'
                  }}>
                    {employees
                      .filter(emp => {
                        const name = (emp.userName || emp.name || '').toLowerCase();
                        const code = (emp.employeeCode || '').toLowerCase();
                        const q = employeeSearch.toLowerCase();
                        return name.includes(q) || code.includes(q);
                      })
                      .map(emp => {
                        const label = `${emp.userName || emp.name} ${emp.employeeCode ? `(${emp.employeeCode})` : ''}`;
                        return (
                          <div
                            key={emp.id}
                            className="custom-select-option"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setFormData(prev => ({ ...prev, employeeId: String(emp.id) }));
                              setEmployeeDropdownOpen(false);
                              setEmployeeSearch('');
                            }}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              borderBottom: '1px solid var(--border, #f0f0f0)',
                              backgroundColor: String(emp.id) === formData.employeeId ? 'var(--menu-active-bg, #e8f4ff)' : 'transparent',
                              color: String(emp.id) === formData.employeeId ? 'var(--menu-active-text, #0056b3)' : 'var(--text-primary, inherit)'
                            }}
                            onMouseEnter={(e) => {
                              if (String(emp.id) !== formData.employeeId) e.target.style.backgroundColor = 'var(--border, #f5f5f5)';
                            }}
                            onMouseLeave={(e) => {
                              if (String(emp.id) !== formData.employeeId) e.target.style.backgroundColor = 'transparent';
                            }}
                          >
                            {label}
                          </div>
                        );
                      })
                    }
                    {employees.filter(emp => {
                      const name = (emp.userName || emp.name || '').toLowerCase();
                      const code = (emp.employeeCode || '').toLowerCase();
                      const q = employeeSearch.toLowerCase();
                      return name.includes(q) || code.includes(q);
                    }).length === 0 && (
                      <div style={{ padding: '8px 12px', color: 'var(--text-secondary, #999)', fontSize: '13px', textAlign: 'center' }}>
                        No employees found
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {formData.payeeType === 'OTHER' && (
              <div className="form-group">
                <label>Payee Name *</label>
                <input type="text" name="payeeName" value={formData.payeeName} onChange={handleChange}
                  placeholder="e.g. Landlord, Utility Company" required />
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Account Head (Expense Type)</label>
                <select name="accountHeadId" value={formData.accountHeadId} onChange={handleChange}>
                  <option value="">Select Account Head</option>
                  {accountHeads.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                  <option value="ADD_NEW">+ Add New Account Head</option>
                </select>
              </div>
              <div className="form-group">
                <label>Project (Optional)</label>
                <select name="projectId" value={formData.projectId} onChange={handleChange}>
                  <option value="">Select Project</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>

            {!isCashAccount && (
              <div className="form-group">
                <label>Reference Number (UTR / Cheque No)</label>
                <input type="text" name="referenceNo" value={formData.referenceNo} onChange={handleChange}
                  placeholder="Enter reference number" />
              </div>
            )}

            <div className="form-group">
              <label>Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange}
                placeholder="Additional notes..." rows="3" />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Saving…' : editingTxn ? 'Update Payment' : 'Record Payment'}
              </button>
              {editingTxn && (
                <button type="button" className="btn-secondary" onClick={handleCancelEdit}>Cancel</button>
              )}
            </div>
          </form>
        </div>

        {/* ── Recent Payments Table ── */}
        <div className="transaction-form-container" style={{ marginTop: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <h2 className="section-title" style={{ margin: 0, border: 'none', padding: 0 }}>Recent Payments</h2>
            <input
              type="text"
              placeholder="🔍 Search by payee or voucher..."
              value={listSearch}
              onChange={(e) => {
                setListSearch(e.target.value);
                setCurrentPage(1);
              }}
              style={{ width: '300px', padding: '8px 12px', fontSize: '13.5px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }}
            />
          </div>

          {listLoading ? (
            <div className="loading">Loading…</div>
          ) : filteredPayments.length === 0 ? (
            <div className="no-data-panel" style={{ textAlign: 'center', padding: '30px' }}>No matching payments found.</div>
          ) : (
            <>
              <div className="table-responsive" style={{ overflowX: 'auto' }}>
                <table className="table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-card-alt)', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Voucher No</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Date</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Payee & Project</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Payment Mode</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Reference No</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'right' }}>Amount</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'center' }}>Status</th>
                      <th style={{ padding: '12px 16px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPayments.map(txn => (
                      <tr key={txn.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', fontFamily: 'monospace', fontWeight: 'bold' }}>{txn.voucherNo}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px' }}>{formatDate(txn.transactionDate)}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px' }}>
                          <div style={{ fontWeight: '500' }}>{payeeName(txn)}</div>
                          {txn.projectName && <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Project: {txn.projectName}</div>}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px' }}>
                          <div>{MODE_LABELS[txn.paymentMode] || txn.paymentMode || '—'}</div>
                          {txn.paymentAccountHeadName && <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{txn.paymentAccountHeadName}</div>}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', color: 'var(--text-secondary)' }}>{txn.referenceNo || '—'}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', fontWeight: 'bold', textAlign: 'right', color: 'var(--accent)' }}>{formatCurrency(txn.amount)}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', textAlign: 'center' }}>{renderStatus(txn)}</td>
                        <td style={{ padding: '12px 16px', fontSize: '13.5px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                              onClick={() => setViewingTxn(txn)}
                              title="View Details"
                            >
                              👁 View
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                              onClick={() => handleEdit(txn)}
                              disabled={txn.status === 'PENDING_L1' || txn.status === 'PENDING_L2'}
                              title={txn.status?.startsWith('PENDING') ? 'Pending approval' : 'Edit'}
                            >
                              ✏️ Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', marginTop: '16px' }}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    style={{ padding: '6px 12px' }}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(page => (
                    <button
                      key={page}
                      type="button"
                      className={`btn btn-sm ${currentPage === page ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => handlePageChange(page)}
                      style={{
                        padding: '6px 12px',
                        background: currentPage === page ? 'var(--accent) !important' : 'transparent !important',
                        color: currentPage === page ? '#fff !important' : 'var(--text-primary) !important',
                        borderColor: currentPage === page ? 'var(--accent) !important' : 'var(--border) !important'
                      }}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    style={{ padding: '6px 12px' }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── View Payment Details Modal ── */}
        {viewingTxn && (
          <div className="modal-overlay" style={{ zIndex: 1100, display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }} onClick={() => setViewingTxn(null)}>
            <div className="modal-content" style={{ maxWidth: '500px', background: 'white', borderRadius: '8px', width: '90%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>Payment Details - {viewingTxn.voucherNo}</h2>
                <button type="button" className="close-button" onClick={() => setViewingTxn(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
              </div>
              <div className="modal-body" style={{ padding: '24px', overflowY: 'auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Voucher No</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{viewingTxn.voucherNo}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Date</span>
                    <span>{formatDate(viewingTxn.transactionDate)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Payee</span>
                    <span>{payeeName(viewingTxn)}</span>
                  </div>
                  {viewingTxn.projectName && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Project</span>
                      <span>{viewingTxn.projectName}</span>
                    </div>
                  )}
                  {viewingTxn.paymentAccountHeadName && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Payment Account</span>
                      <span>{viewingTxn.paymentAccountHeadName}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Payment Mode</span>
                    <span>{MODE_LABELS[viewingTxn.paymentMode] || viewingTxn.paymentMode || '—'}</span>
                  </div>
                  {viewingTxn.referenceNo && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Reference No</span>
                      <span>{viewingTxn.referenceNo}</span>
                    </div>
                  )}
                  {viewingTxn.notes && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: '1px solid var(--border)', paddingBottom: '8px' }}>
                      <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Notes</span>
                      <span style={{ color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{viewingTxn.notes}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '1.2rem', color: 'var(--text-primary)' }}>Amount</span>
                    <span style={{ fontWeight: 'bold', fontSize: '1.2rem', color: 'var(--accent)' }}>{formatCurrency(viewingTxn.amount)}</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: '#ffffff' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setViewingTxn(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showNewAccountHeadModal && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1100 }}
          onClick={(e) => {
            e.stopPropagation();
            setShowNewAccountHeadModal(false);
            setNewAccountHeadForm({ name: '', description: '' });
          }}
        >
          <div
            className="modal-content"
            style={{ maxWidth: '450px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>New Account Head (Expense Type)</h2>
              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setShowNewAccountHeadModal(false);
                  setNewAccountHeadForm({ name: '', description: '' });
                }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Name *</label>
                <input
                  type="text"
                  value={newAccountHeadForm.name}
                  onChange={(e) => setNewAccountHeadForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Cement Purchase"
                  required
                />
              </div>
              <div className="form-group" style={{ marginTop: '14px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Description</label>
                <textarea
                  value={newAccountHeadForm.description}
                  onChange={(e) => setNewAccountHeadForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Describe this expense type..."
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '20px', borderTop: '1px solid var(--border, #e5e7eb)', background: '#ffffff', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowNewAccountHeadModal(false);
                  setNewAccountHeadForm({ name: '', description: '' });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateAccountHead}
                disabled={!newAccountHeadForm.name.trim()}
              >
                Save Account Head
              </button>
            </div>
          </div>
        </div>
      )}
      {renderHistoryModal()}
    </div>
  );
}

export default VendorPaymentsPage;
